import React, { useState } from 'react';
import { Alert, Button, Dialog } from '@betareader/design-system';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { authUtil } from '../../utils';
import './FeedbackModal.css';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  featureName: string;
  url?: string;
}

interface FormData {
  userFeedback: string;
}

interface FormErrors {
  userFeedback?: string;
}

const FeedbackModal: React.FC<Props> = ({
  isOpen,
  onClose,
  featureName,
  url,
}) => {
  const { t } = useTranslation();
  const fieldId = React.useId();
  const errorId = `${fieldId}-error`;
  const [formData, setFormData] = useState<FormData>({ userFeedback: '' });
  const [errors, setErrors] = useState<FormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    if (!formData.userFeedback.trim()) {
      newErrors.userFeedback = t('FeedbackRequired');
    } else if (formData.userFeedback.trim().length < 5) {
      newErrors.userFeedback = t('FeedbackTooShort', { min: 5 });
    } else if (formData.userFeedback.trim().length > 5000) {
      newErrors.userFeedback = t('FeedbackTooLong', { max: 5000 });
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (field: keyof FormData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear field error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      const idToken = await authUtil.getFreshIdToken();
      const response = await fetch(
        `${process.env.REACT_APP_API_HOST}${process.env.REACT_APP_API_PATH}/feedback`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'br-token': idToken,
          },
          body: JSON.stringify({
            featureName,
            userFeedback: formData.userFeedback.trim(),
            url,
          }),
        }
      );

      if (response.ok) {
        toast.success(t('FeedbackSubmittedSuccessfully'));
        onClose();
        setFormData({ userFeedback: '' });
        setErrors({});
      } else {
        const errorData = await response.json();
        if (errorData.error === 'Validation failed' && errorData.details) {
          // Handle validation errors from API
          const newErrors: FormErrors = {};
          errorData.details.forEach((detail: any) => {
            if (detail.field === 'userFeedback') {
              newErrors.userFeedback = detail.message;
            }
          });
          setErrors(newErrors);
        } else {
          toast.error(t('FeedbackSubmissionFailed'));
        }
      }
    } catch (error) {
      console.error('Error submitting feedback:', error);
      toast.error(t('FeedbackSubmissionFailed'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      onClose();
      setFormData({ userFeedback: '' });
      setErrors({});
    }
  };

  return <Dialog open={isOpen} onClose={handleClose}
    title={t('FeedbackModalTitle', { feature: featureName })}
    className="br-feedback-modal"
    actions={<>
      <Button variant="secondary" onClick={handleClose} disabled={isSubmitting}>
        {t('Cancel')}
      </Button>
      <Button onClick={handleSubmit} disabled={isSubmitting} aria-busy={isSubmitting}>
        {t('Submit')}
      </Button>
    </>}
  >
    <div className="br-field">
      <label className="br-field__label" htmlFor={fieldId}>{t('YourFeedback')}</label>
      <textarea id={fieldId} className="br-field__input"
        placeholder={t('FeedbackPlaceholder')}
        value={formData.userFeedback}
        onChange={event => handleInputChange('userFeedback', event.target.value)}
        rows={4} maxLength={5000} disabled={isSubmitting}
        aria-invalid={!!errors.userFeedback}
        aria-describedby={errors.userFeedback ? errorId : undefined} />
      <span className="br-feedback-modal__count">{formData.userFeedback.length}/5000</span>
    </div>
    {errors.userFeedback &&
      <Alert id={errorId} variant="error">{errors.userFeedback}</Alert>}
    <p className="br-feedback-modal__disclaimer">{t('FeedbackDisclaimer')}</p>
  </Dialog>;
};

export default FeedbackModal;
